/**
 * Run N-3 — the PracticeHub push status chip (dashboard rows + assessment page).
 * Renders nothing without a record. Push now is only offered on a terminal
 * record; it calls the recorded manual-push route and hands the fresh record
 * back to the parent.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { pushChipView, type PushRecordView } from '@/lib/pushStatusChip';

const TONE_CLASS: Record<string, string> = {
  done: 'bg-green-100 text-green-800',
  attention: 'bg-amber-100 text-amber-800',
  danger: 'bg-red-100 text-red-800',
};

export function PushStatusChip({ record, assessmentId, onChanged }: {
  record: PushRecordView | null | undefined;
  assessmentId: string;
  onChanged?: (rec: PushRecordView | null) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const view = pushChipView(record);
  if (!view) return null;

  const pushNow = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try { await api.post(`/practicehub/push/${assessmentId}`, {}); } catch { /* the record tells the truth below */ }
    try {
      const r = await api.get<{ record: PushRecordView | null }>(`/practicehub/push-status/${assessmentId}`);
      if (r.success) onChanged?.(r.data?.record ?? null);
    } catch { /* leave the chip as it was */ }
    setBusy(false);
  };

  return (
    <span
      data-testid={`push-status-${view.state}`}
      className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${TONE_CLASS[view.tone]}`}
    >
      {t(view.key, { defaultValue: view.fallback, ...(view.params ?? {}) })}
      {view.canPushNow && (
        <button
          type="button"
          data-testid="push-now"
          disabled={busy}
          onClick={(e) => { void pushNow(e); }}
          className="underline font-semibold disabled:opacity-60"
        >
          {busy ? t('pushStatus.pushing', 'Pushing…') : t('pushStatus.pushNow', 'Push now')}
        </button>
      )}
    </span>
  );
}
